"use client";

import { useRef, useState } from "react";
import {
  Checkbox,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, HINT, LABEL } from "@/components/tool-kit/styles";
import {
  HandwritingHeader,
  HandwritingSheet,
} from "@/components/tools/printables/handwriting-page";
import {
  PaperControls,
  PRINTABLES_COMMON_STRINGS,
  PrintablePages,
  PrintBar,
  useTextMeasure,
} from "@/components/tools/printables/page-kit";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  layoutHandwriting,
  parseEntries,
  type TraceStyle,
} from "@/lib/tool-kit/printables/handwriting";
import {
  type Orientation,
  type PaperSize,
  pageGeometry,
} from "@/lib/tool-kit/printables/page";
import type { FontKind } from "@/lib/tool-kit/printables/text";

/** English strings. A translated content file overrides these through `ui`. */
export const HANDWRITING_WORKSHEET_GENERATOR_STRINGS = {
  ...PRINTABLES_COMMON_STRINGS,
  defaultTitle: "Handwriting Practice",
  sampleText: "Aa\nBb\nCc\nDd",
  wordsLabel: "Letters, words or sentences — one per line",
  wordsHint:
    "Each line becomes a practice line. A line too long for the page wraps onto more lines.",
  presetsLabel: "Start from",
  presets: {
    alphabet: "Aa Bb Cc …",
    lower: "a–z",
    upper: "A–Z",
    numbers: "0–9",
    sentence: "A sentence",
  },
  sentenceText: "The quick brown fox jumps over the lazy dog.",
  heightLabel: "Writing height (top line to baseline)",
  heights: {
    "25": "Extra large — 1 in (25 mm)",
    "20": "Large — 3/4 in (20 mm)",
    "16": "Medium — 5/8 in (16 mm)",
    "12": "Small — 1/2 in (12 mm)",
    "9.5": "Narrow — 3/8 in (9.5 mm)",
  },
  fontLabel: "Letter style",
  fonts: {
    sans: "Print — clean sans-serif",
    serif: "Print — serif",
    script: "Cursive — script font from your device",
  },
  fontHint:
    "Letter shapes come from the fonts installed on your device. The preview shows exactly what will print.",
  sizeLabel: "Letter size on the lines",
  sizes: {
    "0.9": "Smaller (90%)",
    "1": "Standard",
    "1.1": "Larger (110%)",
    "1.2": "Largest (120%)",
  },
  traceLabel: "Tracing style",
  traceStyles: { dashed: "Dashed outline", pale: "Pale grey letters" },
  modelLabel: "Start each line with a solid model copy",
  copiesLabel: "Traced copies per line",
  copiesFill: "Fill the line",
  blankLabel: "Empty ruled lines after each entry",
  titleLabel: "Worksheet title",
  titleHint: "Leave blank for no title.",
  nameLine: "Name and date lines at the top",
  nameLabel: "Name",
  dateLabel: "Date",
  colorLabel: "Line colour",
  colors: { blue: "Blue", grey: "Grey", black: "Black" },
  previewLabel: "Handwriting worksheet preview",
  summary:
    "Each practice line is {height} mm tall, from the top line to the baseline.",
} as const;

const ALPHABET = "abcdefghijklmnopqrstuvwxyz".split("");
const LINE_COLORS = { blue: "#6f93c8", grey: "#9a9a9a", black: "#222222" };
const COPY_CHOICES = ["0", "1", "2", "3", "4", "5", "6", "8"];
const BLANK_CHOICES = ["0", "1", "2", "3"];

type Preset = keyof typeof HANDWRITING_WORKSHEET_GENERATOR_STRINGS.presets;
type Height = keyof typeof HANDWRITING_WORKSHEET_GENERATOR_STRINGS.heights;
type SizeKey = keyof typeof HANDWRITING_WORKSHEET_GENERATOR_STRINGS.sizes;
type ColorKey = keyof typeof LINE_COLORS;

function presetText(preset: Preset, sentence: string): string {
  switch (preset) {
    case "alphabet":
      return ALPHABET.map((c) => `${c.toUpperCase()}${c}`).join("\n");
    case "lower":
      return ALPHABET.join("\n");
    case "upper":
      return ALPHABET.map((c) => c.toUpperCase()).join("\n");
    case "numbers":
      return "0123456789".split("").join("\n");
    default:
      return sentence;
  }
}

/** Space above the first ruled line: the title and the name line. */
function headerHeightFor(title: string, nameLine: boolean): number {
  const block = (title ? 10 : 0) + (nameLine ? 10 : 0);
  return block > 0 ? block + 4 : 0;
}

export default function HandwritingWorksheetGeneratorWidget() {
  const strings = useToolUi(HANDWRITING_WORKSHEET_GENERATOR_STRINGS);
  const measure = useTextMeasure();
  const svgRef = useRef<SVGSVGElement>(null);
  const [text, setText] = useState<string>(strings.sampleText);
  const [paper, setPaper] = useState<PaperSize>("letter");
  const [orientation, setOrientation] = useState<Orientation>("portrait");
  const [height, setHeight] = useState<Height>("16");
  const [fontKind, setFontKind] = useState<FontKind>("sans");
  const [sizeKey, setSizeKey] = useState<SizeKey>("1");
  const [traceStyle, setTraceStyle] = useState<TraceStyle>("dashed");
  const [model, setModel] = useState(true);
  const [copies, setCopies] = useState("0");
  const [blankRows, setBlankRows] = useState("1");
  const [title, setTitle] = useState<string>(strings.defaultTitle);
  const [nameLine, setNameLine] = useState(true);
  const [color, setColor] = useState<ColorKey>("blue");

  const geometry = pageGeometry(paper, orientation);
  const heightMm = Number(height);
  const trimmedTitle = title.trim();
  const headerHeight = headerHeightFor(trimmedTitle, nameLine);
  const layout = layoutHandwriting(
    parseEntries(text),
    {
      height: heightMm,
      fontKind,
      sizeScale: Number(sizeKey),
      model,
      copies: Number(copies),
      blankRows: Number(blankRows),
      headerHeight,
    },
    geometry,
    measure
  );

  const pages = layout.pages.map((page, index) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: sheets are positional and never reordered
    <g key={`sheet-${index}`}>
      {index === 0 ? (
        <HandwritingHeader
          dateLabel={strings.dateLabel}
          geometry={geometry}
          measure={measure}
          nameLabel={strings.nameLabel}
          nameLine={nameLine}
          title={trimmedTitle}
        />
      ) : null}
      <HandwritingSheet
        layout={layout}
        lineColor={LINE_COLORS[color]}
        page={page}
        traceStyle={traceStyle}
      />
    </g>
  ));

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <TextAreaField
          hint={strings.wordsHint}
          id="hw-words"
          label={strings.wordsLabel}
          onChange={setText}
          prose
          rows={5}
          value={text}
        />
        <div className="space-y-2">
          <p className={LABEL}>{strings.presetsLabel}</p>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(strings.presets) as Preset[]).map((preset) => (
              <button
                className={BUTTON_SMALL}
                key={preset}
                onClick={() =>
                  setText(presetText(preset, strings.sentenceText))
                }
                type="button"
              >
                {strings.presets[preset]}
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SelectField
            id="hw-height"
            label={strings.heightLabel}
            onChange={setHeight}
            options={(Object.keys(strings.heights) as Height[]).map(
              (value) => ({
                value,
                label: strings.heights[value],
              })
            )}
            value={height}
          />
          <SelectField
            hint={strings.fontHint}
            id="hw-font"
            label={strings.fontLabel}
            onChange={setFontKind}
            options={(["sans", "serif", "script"] as const).map((value) => ({
              value,
              label: strings.fonts[value],
            }))}
            value={fontKind}
          />
          <SelectField
            id="hw-size"
            label={strings.sizeLabel}
            onChange={setSizeKey}
            options={(Object.keys(strings.sizes) as SizeKey[]).map((value) => ({
              value,
              label: strings.sizes[value],
            }))}
            value={sizeKey}
          />
          <SelectField
            id="hw-trace"
            label={strings.traceLabel}
            onChange={setTraceStyle}
            options={(["dashed", "pale"] as const).map((value) => ({
              value,
              label: strings.traceStyles[value],
            }))}
            value={traceStyle}
          />
          <SelectField
            id="hw-copies"
            label={strings.copiesLabel}
            onChange={setCopies}
            options={COPY_CHOICES.map((value) => ({
              value,
              label: value === "0" ? strings.copiesFill : value,
            }))}
            value={copies}
          />
          <SelectField
            id="hw-blank"
            label={strings.blankLabel}
            onChange={setBlankRows}
            options={BLANK_CHOICES.map((value) => ({ value, label: value }))}
            value={blankRows}
          />
          <PaperControls
            idPrefix="hw"
            onOrientation={setOrientation}
            onPaper={setPaper}
            orientation={orientation}
            paper={paper}
            strings={strings}
          />
          <SelectField
            id="hw-color"
            label={strings.colorLabel}
            onChange={setColor}
            options={(["blue", "grey", "black"] as const).map((value) => ({
              value,
              label: strings.colors[value],
            }))}
            value={color}
          />
        </div>
        <TextField
          hint={strings.titleHint}
          id="hw-title"
          label={strings.titleLabel}
          onChange={setTitle}
          value={title}
        />
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={model}
            label={strings.modelLabel}
            onChange={setModel}
          />
          <Checkbox
            checked={nameLine}
            label={strings.nameLine}
            onChange={setNameLine}
          />
        </div>
        <PrintBar
          fileName={`handwriting-worksheet-${paper}`}
          pageTotal={layout.pages.length}
          paper={paper}
          strings={strings}
          svgRef={svgRef}
        />
        <p className={HINT}>{t(strings.summary, { height: heightMm })}</p>
      </div>
      <PrintablePages
        className={orientation === "landscape" ? "max-w-3xl" : "max-w-xl"}
        geometry={geometry}
        label={strings.previewLabel}
        pages={pages}
        svgRef={svgRef}
      />
    </div>
  );
}
